import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  dropTargetUnder,
  markDropTarget,
  markInsertPoint,
  moveGhostTo,
} from './carry'
import {
  boxBetween,
  boxOnScreen,
  type ContentSpot,
  type HighlightBox,
  sameBox,
  spotInContent,
} from './highlighter'
import { holdsPointer, type PointerSpot, useEdgeScroll } from './pointer'

const DRAG_SLOP = 4

// What moving the pointer with the button down does: carry the pages in hand, draw a box over
// pages, scroll the strip, or nothing at all.
export type DragKind = 'carry' | 'box' | 'pan' | 'none'

interface PressKeys {
  onChip: boolean
  onPage: boolean
  // The page pressed is picked up, or every page of the chip's statement is.
  holdsPicked: boolean
  additive: boolean
  extend: boolean
}

// The box needs ctrl (cmd on a Mac), so a plain drag across the strip picks nothing by mistake.
// A plain drag on a page carries it, and the whole pick when the page is part of one. A plain
// drag on a chip scrolls the strip, unless its statement is already picked up: then it carries.
export const dragKindOf = ({
  onChip,
  onPage,
  holdsPicked,
  additive,
  extend,
}: PressKeys): DragKind => {
  if (additive) return 'box'
  if (extend) return 'none'
  if (holdsPicked) return 'carry'
  if (onChip) return 'pan'
  if (onPage) return 'carry'
  return 'none'
}

interface Press {
  originInContent: ContentSpot
  originOnScreen: PointerSpot
  scrollOrigin: number
  base: ReadonlySet<number>
  page: number | null
  band: string | null
  within: string | null
  additive: boolean
  extend: boolean
  drag: DragKind
  dragged: boolean
}

const runBetween = (from: number, to: number) =>
  Array.from(
    { length: Math.abs(to - from) + 1 },
    (_unused, step) => Math.min(from, to) + step,
  )

const pagePressed = (event: ReactPointerEvent<HTMLElement>) => {
  const card = (event.target as Element).closest('[data-page-number]')
  const page = Number(card?.getAttribute('data-page-number'))
  return Number.isInteger(page) && page > 0 ? page : null
}

const bandPressed = (event: ReactPointerEvent<HTMLElement>) =>
  (event.target as Element)
    .closest('[data-band-statement]')
    ?.getAttribute('data-band-statement') ?? null

// The statement whose band the press landed in, wherever inside it: its chip, a page, or the
// padding round them.
const bandUnder = (event: ReactPointerEvent<HTMLElement>) =>
  (event.target as Element).closest('[data-band]')?.getAttribute('data-band') ??
  null

const holdsEvery = (selection: ReadonlySet<number>, pages: number[]) =>
  pages.length > 0 && pages.every((page) => selection.has(page))

const withBandToggled = (selection: ReadonlySet<number>, pages: number[]) => {
  const next = new Set(selection)
  if (holdsEvery(selection, pages)) {
    for (const page of pages) next.delete(page)
    return next
  }
  for (const page of pages) next.add(page)
  return next
}

const farEnoughToBeADrag = (from: PointerSpot, to: PointerSpot) =>
  Math.abs(to.x - from.x) > DRAG_SLOP || Math.abs(to.y - from.y) > DRAG_SLOP

// The strip only scrolls sideways, so an up or down wobble on a chip is still a click.
const startsADrag = (held: Press, spot: PointerSpot) =>
  held.drag === 'pan'
    ? Math.abs(spot.x - held.originOnScreen.x) > DRAG_SLOP
    : farEnoughToBeADrag(held.originOnScreen, spot)

const sameSet = (one: ReadonlySet<number>, other: ReadonlySet<number>) =>
  one.size === other.size && [...one].every((page) => other.has(page))

interface SelectionOptions {
  scrollElement: () => HTMLElement | null
  // The other pane the carried pages travel over. Whichever of the two the pointer sits in is
  // the one that creeps when the pages are held at its edge.
  carryScrollElement: () => HTMLElement | null
  // Which pages a swept box touches. A virtualized pane must answer from its own layout, or the
  // sweep loses every page the browser has not drawn.
  pagesIn: (box: HighlightBox) => number[]
  // Every page of one statement, all its runs, so a band's handle picks the whole thing up.
  pagesOfBand: (statementId: string) => number[]
  // The statements from one band to another in strip order, for a shift-click across bands.
  bandsBetween: (fromStatementId: string, toStatementId: string) => string[]
  onFocusPage: (page: number) => void
  onDropPages: (target: HTMLElement) => void
  onActivateBand: (statementId: string) => void
  // A chip held to scroll the strip must not let the strip settle under the hand.
  onPanStart: () => void
  onPanEnd: () => void
}

// A click reads a page, ctrl / shift picks pages up, and a plain drag carries the page or the set it
// is in. Pages are a set, not a first and last, because a statement can hold pages that do not sit
// together. A band's chip opens its statement, gathers it with ctrl / shift, and drags the strip.
export const usePageSelection = (options: SelectionOptions) => {
  // Read at event time, so the caller's handlers can be new every render.
  const latest = useRef(options)
  latest.current = options
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set())
  const [highlight, setHighlight] = useState<HighlightBox | null>(null)
  const [carryFrom, setCarryFrom] = useState<PointerSpot | null>(null)
  const [pressing, setPressing] = useState(false)
  const [dragging, setDragging] = useState(false)
  const anchor = useRef<number | null>(null)
  const bandAnchor = useRef<string | null>(null)
  const press = useRef<Press | null>(null)
  const pointer = useRef<PointerSpot>({ x: 0, y: 0 })
  const carryGhost = useRef<HTMLDivElement>(null)
  const hovered = useRef<HTMLElement | null>(null)

  const view = useCallback((page: number) => {
    anchor.current = page
    latest.current.onFocusPage(page)
  }, [])

  const extendTo = useCallback((page: number) => {
    setSelected(new Set(runBetween(anchor.current ?? page, page)))
    latest.current.onFocusPage(page)
  }, [])

  const toggle = useCallback((page: number) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(page)) {
        next.delete(page)
      } else {
        next.add(page)
      }
      return next
    })
    anchor.current = page
    latest.current.onFocusPage(page)
  }, [])

  const clear = useCallback(() => {
    setSelected(new Set())
    anchor.current = null
    bandAnchor.current = null
  }, [])

  const toggleBand = useCallback((statementId: string) => {
    const pages = latest.current.pagesOfBand(statementId)
    setSelected((current) => withBandToggled(current, pages))
    bandAnchor.current = statementId
  }, [])

  const extendToBand = useCallback((statementId: string) => {
    const { bandsBetween, pagesOfBand } = latest.current
    const from = bandAnchor.current ?? statementId
    const reached = bandsBetween(from, statementId)
    setSelected(new Set(reached.flatMap((held) => pagesOfBand(held))))
    bandAnchor.current = statementId
  }, [])

  const sweepTo = useCallback((spot: PointerSpot) => {
    const container = latest.current.scrollElement()
    const held = press.current
    if (!container || !held) return

    const box = boxBetween(held.originInContent, spotInContent(container, spot))
    const swept = latest.current.pagesIn(box)
    const next = new Set(
      held.additive || held.extend ? [...held.base, ...swept] : swept,
    )
    const painted = boxOnScreen(container, box)

    setHighlight((current) => (sameBox(current, painted) ? current : painted))
    setSelected((current) => (sameSet(current, next) ? current : next))
    anchor.current = swept[0] ?? anchor.current
  }, [])

  // Like grabbing the paper: the pages follow the pointer, so moving it left scrolls right.
  const panTo = useCallback((spot: PointerSpot) => {
    const container = latest.current.scrollElement()
    const held = press.current
    if (!container || !held) return
    container.scrollLeft = held.scrollOrigin - (spot.x - held.originOnScreen.x)
  }, [])

  const carryTo = useCallback((spot: PointerSpot) => {
    moveGhostTo(carryGhost.current, spot)
    hovered.current = markDropTarget(hovered.current, dropTargetUnder(spot))
    markInsertPoint(hovered.current, spot)
  }, [])

  const dragTo = useCallback(
    (spot: PointerSpot) => {
      const held = press.current
      if (!held) return
      if (!held.dragged && !startsADrag(held, spot)) return
      if (!held.dragged) {
        held.dragged = true
        if (held.drag === 'pan') latest.current.onPanStart()
        // A pan must not creep at the edges, so only a carry or a box counts as dragging.
        if (held.drag === 'carry' || held.drag === 'box') setDragging(true)
        if (held.drag === 'carry') {
          // A page dragged without picking it first goes alone.
          if (held.page !== null && !held.base.has(held.page)) {
            setSelected(new Set([held.page]))
            anchor.current = held.page
          }
          setCarryFrom(spot)
        }
      }
      if (held.drag === 'carry') carryTo(spot)
      if (held.drag === 'box') sweepTo(spot)
      if (held.drag === 'pan') panTo(spot)
    },
    [carryTo, panTo, sweepTo],
  )

  const finishPress = useCallback(() => {
    const held = press.current
    const landedOn = hovered.current
    press.current = null
    hovered.current = markDropTarget(landedOn, null)
    setPressing(false)
    setDragging(false)
    setHighlight(null)
    setCarryFrom(null)
    if (!held) return
    if (held.drag === 'pan' && held.dragged) latest.current.onPanEnd()

    if (held.band !== null && !held.dragged) {
      if (held.additive) {
        toggleBand(held.band)
      } else if (held.extend) {
        extendToBand(held.band)
      } else {
        bandAnchor.current = held.band
        latest.current.onActivateBand(held.band)
      }
      return
    }

    if (held.drag === 'carry') {
      if (held.dragged) {
        if (landedOn) latest.current.onDropPages(landedOn)
      } else if (held.page !== null) {
        view(held.page)
      }
      return
    }
    if (held.dragged) return

    if (held.page === null) {
      // A modifier means the user is still gathering pages, so a miss must not wipe the lot.
      if (held.additive || held.extend) return
      if (held.within !== null) {
        latest.current.onActivateBand(held.within)
      } else {
        clear()
      }
    } else if (held.additive) {
      toggle(held.page)
    } else if (held.extend && anchor.current !== null) {
      extendTo(held.page)
    } else {
      // Reading a page inside another statement's band also makes that statement the one open.
      if (held.within !== null) latest.current.onActivateBand(held.within)
      view(held.page)
    }
  }, [clear, extendTo, extendToBand, toggle, toggleBand, view])

  const keepUpWithTheScroll = useCallback(
    () => dragTo(pointer.current),
    [dragTo],
  )

  // Whichever pane the pointer sits in creeps, so one carry can reach a band off the right of the
  // strip and a month below the bottom of the map without letting go.
  const creepingPane = useCallback(() => {
    const { scrollElement, carryScrollElement } = latest.current
    if (!carryFrom) return scrollElement()
    const other = carryScrollElement()
    return other && holdsPointer(other, pointer.current)
      ? other
      : scrollElement()
  }, [carryFrom])

  // Only once the drag is real: creeping on a plain press would move the pages under the click.
  useEdgeScroll({
    active: dragging,
    scrollElement: creepingPane,
    pointer,
    onScrolled: keepUpWithTheScroll,
  })

  useEffect(() => {
    if (!pressing) return

    const follow = (event: PointerEvent) => {
      pointer.current = { x: event.clientX, y: event.clientY }
      dragTo(pointer.current)
    }

    window.addEventListener('pointermove', follow)
    window.addEventListener('pointerup', finishPress)
    window.addEventListener('pointercancel', finishPress)
    // Letting go over the devtools or another window never sends pointerup, which would leave
    // the highlighter painted on screen.
    window.addEventListener('blur', finishPress)
    return () => {
      window.removeEventListener('pointermove', follow)
      window.removeEventListener('pointerup', finishPress)
      window.removeEventListener('pointercancel', finishPress)
      window.removeEventListener('blur', finishPress)
    }
  }, [pressing, dragTo, finishPress])

  useEffect(() => {
    const clearOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clear()
    }
    window.addEventListener('keydown', clearOnEscape)
    return () => window.removeEventListener('keydown', clearOnEscape)
  }, [clear])

  // One gesture for the whole pane: press a page to read it, drag a page to carry it (and the rest
  // of the set, when it is picked up) to a month or another statement, press a band's label chip and drag to scroll the strip,
  // hold ctrl and drag to sweep a box over pages, press the empty space and let go to drop the
  // selection.
  const pressPages = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const container = latest.current.scrollElement()
      if (!container || event.button !== 0) return
      const spot = { x: event.clientX, y: event.clientY }
      const page = pagePressed(event)
      const band = bandPressed(event)
      const additive = event.metaKey || event.ctrlKey
      const extend = event.shiftKey
      const holdsPicked =
        page !== null
          ? selected.has(page)
          : band !== null &&
            holdsEvery(selected, latest.current.pagesOfBand(band))
      const drag = dragKindOf({
        onChip: band !== null,
        onPage: page !== null,
        holdsPicked,
        additive,
        extend,
      })
      // Captured so the chip keeps its grabbing cursor wherever the pointer goes.
      const pressed = event.target as Element
      if (drag === 'pan') pressed.setPointerCapture(event.pointerId)
      pointer.current = spot
      press.current = {
        originInContent: spotInContent(container, spot),
        originOnScreen: spot,
        scrollOrigin: container.scrollLeft,
        base: selected,
        page,
        band,
        within: bandUnder(event),
        additive,
        extend,
        drag,
        dragged: false,
      }
      setPressing(true)
    },
    [selected],
  )

  return {
    selected,
    highlight,
    carryFrom,
    carryGhost,
    pressPages,
    view,
    toggle,
    clear,
  }
}
